import { useEffect, useState } from "react";
import Sider from "../../comps/sider/main";
import './main.css';
import { Link } from 'react-router-dom';
import { Avatar, Typography, Alert, Skeleton, Table, Card, message, Col, Row, Empty, Button, Modal, Tag, Space, Tabs, Popconfirm, Spin, Input, Select, Popover, Divider } from "antd";
import { UserAddOutlined, CopyOutlined, CheckCircleOutlined, CloseCircleOutlined, MailOutlined, CrownOutlined, DeleteOutlined, EditOutlined, ExclamationCircleOutlined, SendOutlined, TeamOutlined, SettingOutlined } from '@ant-design/icons';
import callAPI from "../../comps/fetch/main";
import getActiveUserId from "../../funcs/getActiveUserId";

const { Title, Text } = Typography;
const { confirm } = Modal;

function TeamSettings() {
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [workspaces, setWorkspaces] = useState([]);
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("admin");
  const [inviteLink, setInviteLink] = useState("");
  const [creatingWorkspace, setCreatingWorkspace] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState("");
  const [workspaceName, setWorkspaceName] = useState("");
  const [savingWorkspace, setSavingWorkspace] = useState(false);
  const [userRole, setUserRole] = useState("viewer");
  const [activeTab, setActiveTab] = useState("members");

  const isAdmin = userRole === "owner" || userRole === "admin";
  const isOwner = userRole === "owner";

  useEffect(() => { loadWorkspaces(); }, []);

  useEffect(() => {
    if (currentWorkspace) {
      loadMembers();
      loadInvitations();
      setWorkspaceName(currentWorkspace.name || "");
    }
  }, [currentWorkspace]);

  const loadWorkspaces = () => {
    const ownerId = getActiveUserId();
    if (!ownerId) return;
    callAPI({
      url: `team/workspace-by-owner?owner_id=${ownerId}`,
      method: "GET",
      loadingState: setLoading,
      dataState: (data) => {
        if (data.message === "Success" || data.code === 200 || data.code === "200") {
          const wsList = (data.data || []).map(ws => ({ ...ws, id: ws.workspace_id }));
          setWorkspaces(wsList);
          const savedId = localStorage.getItem("current_workspace_id");
          const ws = wsList.find(w => w.id == savedId) || wsList[0];
          if (ws) {
            setCurrentWorkspace(ws);
            localStorage.setItem("current_workspace_id", ws.id);
          }
        } else if (data.code === 403 && data.message === "WORKSPACE_ACCESS_DENIED") {
          messageApi.error("Access denied. Refreshing workspaces...");
          loadWorkspaces();
        }
      }
    });
  };

  const loadMembers = () => {
    callAPI({
      url: `team/members?workspace_id=${currentWorkspace.id}`,
      method: "GET",
      loadingState: setLoading,
      dataState: (data) => {
        if (data.code === 200 || data.code === "200") {
          setMembers(data.data || []);
          try {
            const me = (data.data || []).find(m => m.user_id == getActiveUserId(true) || m.id == getActiveUserId(true));
            if (me) setUserRole(me.role);
          } catch (e) {}
        }
      }
    });
  };

  const loadInvitations = () => {
    callAPI({
      url: `team/invite?workspace_id=${currentWorkspace.id}`,
      method: "GET",
      loadingState: setLoading,
      dataState: (data) => {
        if (data.code === 200 || data.code === "200") {
          setInvitations(data.data || []);
        }
      }
    });
  };

  const handleWorkspaceChange = (wsId) => {
    const ws = workspaces.find(w => w.id == wsId);
    if (ws) {
      setCurrentWorkspace(ws);
      localStorage.setItem("current_workspace_id", ws.id);
    }
  };

  const handleCreateWorkspace = () => {
    if (!newWorkspaceName.trim()) {
      messageApi.warning("Please enter a workspace name");
      return;
    }
    setCreatingWorkspace(true);
    callAPI({
      url: "team/workspace",
      method: "POST",
      body: { name: newWorkspaceName },
      loadingState: setCreatingWorkspace,
      dataState: (data) => {
        setCreatingWorkspace(false);
        if (data.code === 200 || data.code === "200") {
          messageApi.success("Workspace created!");
          setNewWorkspaceName("");
          loadWorkspaces();
        } else {
          messageApi.error(data.message || "Failed to create workspace");
        }
      }
    });
  };

  const handleSaveWorkspaceName = () => {
    if (!currentWorkspace) return;
    setSavingWorkspace(true);
    callAPI({
      url: `team/workspace/${currentWorkspace.id}`,
      method: "PUT",
      body: { name: workspaceName },
      loadingState: setSavingWorkspace,
      dataState: (data) => {
        setSavingWorkspace(false);
        if (data.code === 200 || data.code === "200") {
          messageApi.success("Workspace name updated!");
          loadWorkspaces();
        } else {
          messageApi.error(data.message || "Failed to update workspace");
        }
      }
    });
  };

  const handleInvite = () => {
    if (!inviteEmail.trim()) {
      messageApi.warning("Please enter an email address");
      return;
    }
    setInviteLoading(true);
    callAPI({
      url: "team/invite",
      method: "POST",
      body: { email: inviteEmail, role: inviteRole, workspace_id: currentWorkspace.id },
      loadingState: setInviteLoading,
      dataState: (data) => {
        setInviteLoading(false);
        if (data.code === 200 || data.code === "200") {
          messageApi.success("Invitation sent!");
          loadInvitations();
          setInviteModalOpen(false);
          setInviteEmail("");
          setInviteRole("admin");
          setInviteLink("");
        } else {
          messageApi.error(data.message || "Failed to send invitation");
        }
      }
    });
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    messageApi.success("Link copied to clipboard!");
  };

  const handleChangeRole = (memberId, newRole) => {
    callAPI({
      url: `team/members/${memberId}?workspace_id=${currentWorkspace.id}`,
      method: "PUT",
      body: { role: newRole },
      loadingState: setLoading,
      dataState: (data) => {
        if (data.code === 200 || data.code === "200") {
          messageApi.success("Role updated!");
          loadMembers();
        } else {
          messageApi.error(data.message || "Failed to update role");
        }
      }
    });
  };

  const handleRemoveMember = (memberId) => {
    callAPI({
      url: `team/members/${memberId}?workspace_id=${currentWorkspace.id}`,
      method: "DELETE",
      loadingState: setLoading,
      dataState: (data) => {
        if (data.code === 200 || data.code === "200") {
          messageApi.success("Member removed!");
          loadMembers();
        } else {
          messageApi.error(data.message || "Failed to remove member");
        }
      }
    });
  };

  const handleResendInvite = (invitationId) => {
    callAPI({
      url: "team/invite",
      method: "PUT",
      body: { invitation_id: invitationId },
      loadingState: setLoading,
      dataState: (data) => {
        if (data.code === 200 || data.code === "200") {
          messageApi.success("Invitation resent!");
          loadInvitations();
        } else {
          messageApi.error(data.message || "Failed to resend invitation");
        }
      }
    });
  };

  const handleCancelInvite = (invitationId) => {
    callAPI({
      url: `team/invite?invitation_id=${invitationId}`,
      method: "DELETE",
      loadingState: setLoading,
      dataState: (data) => {
        if (data.code === 200 || data.code === "200") {
          messageApi.success("Invitation cancelled!");
          loadInvitations();
        } else {
          messageApi.error(data.message || "Failed to cancel invitation");
        }
      }
    });
  };

  const handleDeleteWorkspace = () => {
    confirm({
      title: "Delete Workspace",
      icon: <ExclamationCircleOutlined />,
      content: "Are you sure you want to delete this workspace? This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => {
        callAPI({
          url: `team/workspace/${currentWorkspace.id}`,
          method: "DELETE",
          loadingState: setLoading,
          dataState: (data) => {
            if (data.code === 200 || data.code === "200") {
              messageApi.success("Workspace deleted!");
              localStorage.removeItem("current_workspace_id");
              setCurrentWorkspace(null);
              loadWorkspaces();
            } else {
              messageApi.error(data.message || "Failed to delete workspace");
            }
          }
        });
      }
    });
  };

  const getAvatarClass = (role) => `ts-member-avatar ts-member-avatar-${role || 'viewer'}`;

  const getRoleBadge = (role) => (
    <span className={`ts-role-badge ts-role-${role || 'viewer'}`}>{role?.toUpperCase()}</span>
  );

  const getStatusBadge = (status) => (
    <span className={`ts-status-badge ts-status-${status || 'pending'}`}>{status?.charAt(0).toUpperCase() + status?.slice(1)}</span>
  );

  const memberColumns = [
    {
      title: "Member",
      dataIndex: "name",
      render: (_, record) => (
        <div className="ts-member-info">
          <Avatar className={getAvatarClass(record.role)} size={40}>
            {record.name ? record.name.charAt(0).toUpperCase() : <UserAddOutlined />}
          </Avatar>
          <div className="ts-member-details">
            <span className="ts-member-name">{record.name || "—"}</span>
            <span className="ts-member-email">{record.email}</span>
          </div>
        </div>
      )
    },
    {
      title: "Role",
      dataIndex: "role",
      width: 140,
      render: (role) => getRoleBadge(role)
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 140,
      render: (status) => getStatusBadge(status)
    },
    {
      title: "Actions",
      width: 220,
      render: (_, record) => {
        const isRemoved = record.status && record.status.toLowerCase() !== 'active';
        if (isRemoved) return <span className="ts-you-badge" style={{ color: '#ef4444' }}>Removed</span>;
        return isAdmin && !record.is_current_user ? (
          <div className="ts-action-group">
            <Select
              className="ts-role-select"
              value={record.role}
              onChange={(val) => handleChangeRole(record.user_id || record.id, val)}
              style={{ width: 100 }}
              size="small"
              disabled={record.role === "owner"}
            >
              <Select.Option value="admin">Admin</Select.Option>
            </Select>
            {record.role !== "owner" && (
              <Popconfirm
                title="Remove this member?"
                description="They will lose access to this workspace."
                okText="Remove"
                cancelText="Cancel"
                onConfirm={() => handleRemoveMember(record.user_id || record.id)}
              >
                <Button className="ts-remove-btn" size="small" icon={<DeleteOutlined />}>
                  Remove
                </Button>
              </Popconfirm>
            )}
          </div>
        ) : record.is_current_user ? (
          <span className="ts-you-badge">You</span>
        ) : null;
      }
    }
  ];

  const invitationColumns = [
    {
      title: "Email",
      dataIndex: "email",
      render: (email) => (
        <div className="ts-member-info">
          <Avatar className="ts-member-avatar" style={{ background: '#818cf8' }} size={40}>
            <MailOutlined />
          </Avatar>
          <span className="ts-member-name">{email}</span>
        </div>
      )
    },
    {
      title: "Role",
      dataIndex: "role",
      width: 140,
      render: (role) => getRoleBadge(role)
    },
    {
      title: "Invited",
      dataIndex: "created_at",
      width: 120,
      render: (date) => <Text type="secondary" style={{ fontSize: 13 }}>{date ? new Date(date).toLocaleDateString() : "—"}</Text>
    },
    {
      title: "Expires",
      dataIndex: "expires_at",
      width: 120,
      render: (date) => <Text type="secondary" style={{ fontSize: 13 }}>{date ? new Date(date).toLocaleDateString() : "—"}</Text>
    },
    {
      title: "Actions",
      width: 200,
      render: (_, record) => (
        <div className="ts-action-group">
          <Button className="ts-resend-btn" size="small" icon={<SendOutlined />} onClick={() => handleResendInvite(record.invitation_id || record.id)}>
            Resend
          </Button>
          <Popconfirm
            title="Cancel this invitation?"
            okText="Cancel Invite"
            cancelText="Keep"
            onConfirm={() => handleCancelInvite(record.invitation_id || record.id)}
          >
            <Button className="ts-cancel-btn" danger size="small" icon={<CloseCircleOutlined />}>
              Cancel
            </Button>
          </Popconfirm>
        </div>
      )
    }
  ];

  const workspaceOptions = workspaces.map(ws => ({ value: ws.id, label: ws.name }));

  // Empty: no workspaces
  if (!loading && workspaces.length === 0 && !currentWorkspace) {
    return (
      <div className="page-layout">
        <Sider />
        <div className="page-content">
          <div className="ts-empty-state">
            <div className="ts-empty-card">
              <div className="ts-empty-icon"><TeamOutlined /></div>
              <div className="ts-empty-title">Create Your First Workspace</div>
              <div className="ts-empty-desc">Workspaces help you collaborate with your team.<br />Create one to get started.</div>
              <Input
                className="ts-empty-input"
                placeholder="Workspace name"
                value={newWorkspaceName}
                onChange={(e) => setNewWorkspaceName(e.target.value)}
                onPressEnter={handleCreateWorkspace}
              />
              <Button className="ts-empty-create-btn" type="primary" block onClick={handleCreateWorkspace} loading={creatingWorkspace}>
                Create Workspace
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Empty: no workspace selected
  if (!currentWorkspace) {
    return (
      <div className="page-layout">
        <Sider />
        <div className="page-content">
          <div className="ts-empty-state">
            <div className="ts-empty-card">
              <div className="ts-empty-icon"><TeamOutlined /></div>
              <div className="ts-empty-title">Select a Workspace</div>
              <div className="ts-empty-desc">Choose a workspace to manage your team.</div>
              <Select
                placeholder="Select workspace"
                style={{ width: "100%" }}
                onChange={handleWorkspaceChange}
                options={workspaceOptions}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-layout">
      {contextHolder}
      <Sider />
      <div className="page-content">
        <div className="team-settings-container">
          {/* Header */}
          <div className="ts-header">
            <div className="ts-header-top">
              <div className="ts-header-left">
                <div className="ts-header-icon">
                  {isOwner ? <CrownOutlined /> : <TeamOutlined />}
                </div>
                <div>
                  <h2 className="ts-header-title">{currentWorkspace.name}</h2>
                  <div className="ts-header-subtitle">Team Settings</div>
                </div>
              </div>
              <Select
                className="ts-workspace-select"
                value={currentWorkspace.id}
                onChange={handleWorkspaceChange}
                style={{ width: 220 }}
                options={workspaceOptions}
              />
            </div>
          </div>

          {/* Tabs */}
          <Tabs
            className="ts-tabs"
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: "members",
                label: <span><TeamOutlined style={{ marginRight: 6 }} />Members</span>,
                children: (
                  <div className="ts-section-card">
                    <div className="ts-section-header">
                      <span className="ts-section-title">
                        Team Members<span className="ts-section-count">({members.length})</span>
                      </span>
                      {isAdmin && (
                        <Button className="ts-invite-btn" type="primary" icon={<UserAddOutlined />} onClick={() => setInviteModalOpen(true)}>
                          Invite member for free
                        </Button>
                      )}
                    </div>
                    <Table
                      columns={memberColumns}
                      dataSource={members}
                      rowKey="user_id"
                      pagination={false}
                      locale={{ emptyText: <Empty description="No members yet" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                    />
                  </div>
                )
              },
              {
                key: "invitations",
                label: <span><MailOutlined style={{ marginRight: 6 }} />Invitations ({invitations.length})</span>,
                children: (
                  <div className="ts-section-card">
                    <div className="ts-section-header">
                      <span className="ts-section-title">
                        Pending Invitations<span className="ts-section-count">({invitations.length})</span>
                      </span>
                    </div>
                    <Table
                      columns={invitationColumns}
                      dataSource={invitations}
                      rowKey="id"
                      pagination={false}
                      locale={{ emptyText: <Empty description="No pending invitations" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                    />
                  </div>
                )
              },
              {
                key: "settings",
                label: <span><SettingOutlined style={{ marginRight: 6 }} />Settings</span>,
                children: (
                  <>
                    <div className="ts-settings-card">
                      <div className="ts-settings-card-title"><SettingOutlined /> Workspace Settings</div>
                      <label className="ts-settings-label">Workspace Name</label>
                      <Input
                        className="ts-settings-input"
                        value={workspaceName}
                        onChange={(e) => setWorkspaceName(e.target.value)}
                        disabled={!isAdmin}
                        placeholder="Enter workspace name"
                      />
                      {isAdmin && (
                        <Button className="ts-save-btn" type="primary" onClick={handleSaveWorkspaceName} loading={savingWorkspace}>
                          Save Changes
                        </Button>
                      )}
                    </div>

                    {isOwner && (
                      <div className="ts-danger-card">
                        <div className="ts-danger-title"><ExclamationCircleOutlined /> Danger Zone</div>
                        <div className="ts-danger-text">
                          Deleting a workspace is permanent and cannot be undone. All members will lose access and all data will be removed.
                        </div>
                        <Popconfirm
                          title="Delete this workspace?"
                          description="This action cannot be undone."
                          okText="Delete"
                          okType="danger"
                          cancelText="Cancel"
                          onConfirm={handleDeleteWorkspace}
                        >
                          <Button className="ts-danger-btn" danger icon={<DeleteOutlined />}>
                            Delete Workspace
                          </Button>
                        </Popconfirm>
                      </div>
                    )}
                  </>
                )
              }
            ]}
          />
        </div>

        {/* Invite Modal */}
        <Modal
          className="ts-invite-modal"
          title="Invite Team Member"
          open={inviteModalOpen}
          onCancel={() => {
            setInviteModalOpen(false);
            setInviteEmail("");
            setInviteRole("admin");
            setInviteLink("");
          }}
          footer={inviteLink ? null : [
            <Button key="cancel" onClick={() => setInviteModalOpen(false)} style={{ borderRadius: 10 }}>Cancel</Button>,
            <Button key="invite" type="primary" loading={inviteLoading} onClick={handleInvite} style={{ borderRadius: 10, background: '#6366f1', border: 'none' }}>
              Send Invitation
            </Button>
          ]}
        >
          {inviteLink ? (
            <div>
              <Alert
                message="Invitation sent successfully!"
                type="success"
                showIcon
                icon={<CheckCircleOutlined />}
                style={{ marginBottom: 16, borderRadius: 10 }}
              />
              <Text>Share this link with your team member:</Text>
              <Input
                value={inviteLink}
                readOnly
                addonAfter={<Button size="small" icon={<CopyOutlined />} onClick={handleCopyLink} type="text">Copy</Button>}
                style={{ marginTop: 8 }}
              />
              <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>Link expires in 72 hours</Text>
            </div>
          ) : (
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <div>
                <label className="ts-invite-label">Email Address</label>
                <Input
                  placeholder="colleague@example.com"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  prefix={<MailOutlined style={{ color: '#8c8ca1' }} />}
                />
              </div>
              <div>
                <label className="ts-invite-label">Role</label>
                <Select
                  value={inviteRole}
                  onChange={setInviteRole}
                  style={{ width: "100%" }}
                >
                  <Select.Option value="admin">Admin — Full access to workspace</Select.Option>
                </Select>
              </div>
            </Space>
          )}
        </Modal>
      </div>
    </div>
  );
}

export default TeamSettings;
